import ElakayImage from '../../../components/images/ElakayImage';
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageRenderer from "../../pageInside/PageBuilder/PageRenderer";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { buildStorefrontPageData } from "../center/storefrontData";
import { buildProviderStoreConfig, ProviderStats, StorefrontProductList } from "./providerStoreConfig";
import { navigationPages, pageHasProductBlock, pagesOf, resolveActivePageId } from "./storefrontPages";
import { providerCatalogPath, providerStorefrontPath } from "../providerWorkspace";
import "./providerStorefront.css";

// Same device breakpoints as the Seller shop (ShopUser.js), so templates' mobileStyle applies for real.
const deviceFor = (width) => (width <= 480 ? "mobile" : width <= 768 ? "tablet" : "desktop");
const useDevice = () => {
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const on = () => setWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return deviceFor(width);
};

// THE Provider storefront website. One component, one renderer (PageRenderer) for:
//   - the public storefront (/provider-shop/:id)            mode "public", published pages
//   - the Provider's Customer View (/provider/my-shop)       mode "owner",  published (or draft preview)
//   - the Page Builder preview (/provider/my-shop/builder)   mode "owner",  current draft
// Only the data version, the owner actions and the page source differ -- never the design.
//
//   summary   storefront response: { provider, shop, stats, settings, custom }
//   custom    override of summary.custom (draft preview): { doc, pages, homePageId, htmlSource, htmlSourcePages }
//   pageId    page to show (null = Home); onSelectPage(id) switches pages without a reload
// Shop website slots (the full shop -- ProviderShop.jsx -- fills them; the Builder preview keeps the
// built-in ones):
//   showNav        page menu inside the storefront header (false when the shop header carries it)
//   renderHeader   () => node replacing the built-in identity header (still subject to showHeader)
//   renderCatalog  () => node replacing the built-in catalogue (same placement rules)
//   fallbackHero   node shown above the catalogue when there is no custom page (default storefront)
export default function ProviderStorefrontView({ summary, custom: customOverride, products = [], categories = [], pageId = null, onSelectPage, mode = "public", infoSource, token = null, productsLoading = false, showNav = true, renderHeader = null, renderCatalog = null, fallbackHero = null }) {
  const navigate = useNavigate();
  const device = useDevice();
  const storeConfig = useMemo(() => buildProviderStoreConfig({ mode, navigate }), [mode, navigate]);
  const custom = customOverride !== undefined ? customOverride : summary?.custom;
  const settings = summary?.settings || { showHeader: true, catalog: { show: true, position: "after" } };
  const doc = custom?.doc && pagesOf(custom.doc).length ? custom.doc : null;
  const rows = custom?.pages || [];
  const activeId = doc ? resolveActivePageId(doc, rows, pageId, custom?.homePageId) : null;
  const activePage = doc ? pagesOf(doc).find((p) => String(p.id) === activeId) : null;
  const pageData = useMemo(() => buildStorefrontPageData(summary, products, categories), [summary, products, categories]);
  const provider = pageData.provider;
  const nav = doc ? navigationPages(rows).filter((r) => pagesOf(doc).some((p) => String(p.id) === String(r.id))) : [];

  // Automatic B2B catalogue: always on the default storefront; on a custom storefront according to
  // the Provider's settings, and never twice when the page already places a product element.
  const catalogOn = !doc || (settings.catalog?.show !== false && settings.catalog?.position !== "builder" && !pageHasProductBlock(activePage));
  const catalogFirst = Boolean(doc) && settings.catalog?.position === "before";
  const headerOn = !doc || settings.showHeader !== false;
  const select = (id) => { if (onSelectPage) onSelectPage(id); };

  const catalog = catalogOn && (renderCatalog ? renderCatalog() : (
    <section className="psf-catalog" aria-labelledby="psf-catalog-title">
      <div className="psf-catalog__head">
        <h2 id="psf-catalog-title">{text("h_psf_catalog", "Explore Provider Products")}</h2>
        {mode !== "owner" && provider._id && <Link to={providerCatalogPath(provider._id)}>{text("h_psf_full_catalog", "Full catalogue, search & filters")}</Link>}
      </div>
      {productsLoading && !products.length
        ? <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>
        : <StorefrontProductList products={pageData.providerProducts} mode={mode} capabilities={provider.capabilities} />}
    </section>
  ));

  return (
    <div className={`psf psf--${device}`} data-storefront-mode={mode}>
      {headerOn && renderHeader && renderHeader()}
      {headerOn && !renderHeader && (
        <header className="psf-header">
          <div className="psf-header__brand">
            {provider.avatar
              ? <ElakayImage preset="avatar" className="psf-header__logo" src={provider.avatar} alt="" />
              : <span className="psf-header__logo psf-header__logo--ph" aria-hidden="true">{provider.monogram || "P"}</span>}
            <div>
              <span className="psf-header__name">{provider.shopName}</span>
              {provider.location && <span className="psf-header__location">{provider.location}</span>}
            </div>
          </div>
          {showNav && nav.length > 0 && (
            <nav className="psf-nav" aria-label={text("h_psf_nav", "Storefront navigation")}>
              {nav.map((r) => (
                <a key={r.id} href={provider._id ? providerStorefrontPath(provider._id, r.id) : "#"} aria-current={String(r.id) === activeId ? "page" : undefined}
                  onClick={(e) => { e.preventDefault(); select(r.id); }}>{r.name}</a>
              ))}
            </nav>
          )}
          {!doc && <ProviderStats stats={provider.stats} />}
        </header>
      )}
      {catalogFirst && catalog}
      {doc && activePage && (
        <div className="psf-page">
          <PageRenderer
            key={activeId}
            doc={doc}
            pageId={activeId}
            pageData={pageData}
            infoSource={infoSource}
            token={token}
            device={device}
            htmlSource={custom?.htmlSource}
            htmlSourcePages={custom?.htmlSourcePages}
            storeConfig={storeConfig}
            onNavigate={(id) => select(id)}
          />
        </div>
      )}
      {!doc && fallbackHero}
      {!catalogFirst && catalog}
    </div>
  );
}
